import type { Middleware } from '@reduxjs/toolkit'
import type { MessageEngine } from '@/engine/MessageEngine/MessageEngine'
import type { ChatEngine } from '@/engine/ChatEngine/ChatEngine'
import { createActionHandler } from '@/store/middleware/utils'
import {
  _addMUChat,
  _addPrivateChat,
  addChats,
  clear,
  deleteChats,
  setMUChats,
  setPrivateChats,
  setThreadsLoaded,
  updateChat,
  updateChats
} from '@/store/chats/chats'
import {
  set as setMessages,
  add as addMessages,
  update as updateMessage,
  _updateById as updateById,
  _remove as removeMessage,
  clear as clearMessages,
} from '@/store/messages/messages'
import {
  set as setBlockList,
  update as updateBlockList,
  remove as removeBlockList
} from '@/store/blockList/blockList'
import {
  setNews,
  _changeNewsViewed,
  _readAllNews
} from '@/store/news/news'
import {
  setUser,
  setAdditionalFields
} from '@/store/user/user'

interface Deps {
  messageEngine: MessageEngine
  chatEngine: ChatEngine
}

export const createChatsMiddleware = ({ chatEngine }: Deps): Middleware => {
  let actions: ((action: unknown) => void)[] | null = null

  const ensureActions = () => {
    if (!actions) {
      actions = [
        createActionHandler({
          mode: 'last',
          action: clear,
          onProcess: chatEngine.onClear
        }),
        createActionHandler({
          mode: 'last',
          action: setPrivateChats,
          onProcess: chatEngine.onSetPrivateChats
        }),
        createActionHandler({
          mode: 'last',
          action: setMUChats,
          onProcess: chatEngine.onSetMUChats
        }),
        createActionHandler({
          mode: 'last',
          action: setThreadsLoaded,
          onProcess: chatEngine.onSetThreadsLoaded
        }),
        createActionHandler({
          action: _addPrivateChat,
          getKey: (chat) => chat.$jid,
          onProcess: chatEngine.onAddChat
        }),
        createActionHandler({
          action: _addMUChat,
          getKey: (chat) => chat.$jid,
          onProcess: chatEngine.onAddChat
        }),
        createActionHandler({
          mode: 'all',
          action: addChats,
          onProcess: chatEngine.onAddChats
        }),
        createActionHandler({
          mode: 'all',
          action: deleteChats,
          onProcess: chatEngine.onDeleteChats
        }),
        createActionHandler({
          action: updateChat,
          getKey: ({ chatJid }) => chatJid,
          onProcess: chatEngine.onUpdateChat
        }),
        createActionHandler({
          mode: 'all',
          action: updateChats,
          onProcess: chatEngine.onUpdateChats
        }),
        createActionHandler({
          mode: 'last',
          action: setMessages,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: addMessages,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: updateMessage,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: updateById,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: removeMessage,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: clearMessages,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: setBlockList,
          onProcess: chatEngine.onUpdateBlockList
        }),
        createActionHandler({
          mode: 'last',
          action: updateBlockList,
          onProcess: chatEngine.onUpdateBlockList
        }),
        createActionHandler({
          mode: 'last',
          action: removeBlockList,
          onProcess: chatEngine.onUpdateBlockList
        }),
        createActionHandler({
          mode: 'last',
          action: setNews,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: _changeNewsViewed,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: _readAllNews,
          onProcess: chatEngine.onUpdateMessages
        }),
        createActionHandler({
          mode: 'last',
          action: setUser,
          onProcess: chatEngine.onUpdateUser
        }),
        createActionHandler({
          mode: 'last',
          action: setAdditionalFields,
          onProcess: chatEngine.onUpdateUser
        })
      ]
    }
    return actions
  }

  return _store => next => action => {
    const result = next(action)

    ensureActions().forEach(run => {
      run(action)
    })

    return result
  }
}
